/*
 * AdvaHealth Solutions Pty. Ltd. ("AHS") CONFIDENTIAL
 * Copyright (c) 2022 AdvaHealth Solutions Pty. Ltd. All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains the property of AHS. The intellectual and technical concepts contained
 * herein are proprietary to AHS and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material is strictly forbidden unless prior written permission is obtained
 * from AHS.  Access to the source code contained herein is hereby forbidden to anyone except current AHS employees, managers or contractors who have executed
 * Confidentiality and Non-disclosure agreements explicitly covering such access.
 *
 * The copyright notice above does not evidence any actual or intended publication or disclosure  of  this source code, which includes
 * information that is confidential and/or proprietary, and is a trade secret, of AHS. ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC  PERFORMANCE,
 * OR PUBLIC DISPLAY OF OR THROUGH USE  OF THIS  SOURCE CODE  WITHOUT  THE EXPRESS WRITTEN CONSENT OF COMPANY IS STRICTLY PROHIBITED, AND IN VIOLATION OF APPLICABLE
 * LAWS AND INTERNATIONAL TREATIES.  THE RECEIPT OR POSSESSION OF  THIS SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS
 * TO REPRODUCE, DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT  MAY DESCRIBE, IN WHOLE OR IN PART.
 */

import { DateTime } from "luxon";
import {
  DateRange,
  OptionTypeBase,
  ConstraintViolationError,
  IConstraintViolationMessage,
  IErrorMessage,
  isValid,
} from "@advapacs/common";
import { Account, IFeature } from "../pages/login/ducks/slices";
import { API_BASE_URL, APP_ENVIRONMENT } from "../config/constants";

export const findConstraintViolationErrorMessage = (
  errorMessage: IErrorMessage | IConstraintViolationMessage | null,
  field: string
): ConstraintViolationError | undefined | null => {
  if (errorMessage?.type === "ConstraintViolationException")
    return (errorMessage as IConstraintViolationMessage).errors.find((e) =>
      e.field.includes(field)
    );
  return null;
};

// create a wordArray that is Big-Endian (because it's used with CryptoJS which is all BE)
// From: https://gist.github.com/creationix/07856504cf4d5cede5f9#file-encode-js
export const convertUint8ArrayToWordArray = (
  u8Array: Uint8Array
): { words: number[]; sigBytes: number } => {
  const words = [];
  let i = 0;
  const len = u8Array.length;

  while (i < len) {
    words.push(
      (u8Array[i++] << 24) |
        (u8Array[i++] << 16) |
        (u8Array[i++] << 8) |
        u8Array[i++]
    );
  }

  return {
    sigBytes: u8Array.length,
    words,
  };
};

export const hasPermission = (
  permissions: string[] = [],
  can?: string[]
): boolean => {
  return !can || !!can.some((item) => permissions.includes(item));
};

export const hasTenantPermission = (
  features: IFeature[],
  tenantId: string,
  can: string[]
) => {
  const feature = features.find(
    (item) => item.clinicianPortal?.id === tenantId
  );

  return feature && hasPermission(feature.permissions, can);
};

export const hasAccountPermission = (account: Account, can: string[]) => {
  return account && hasPermission(account.permissions, can);
};

export const isOwner = (account: Account | null): boolean | undefined => {
  return account?.owner;
};

export const parseDateFromString = (
  dirtyDate?: string,
  fmt?: string
): Date | undefined => {
  if (dirtyDate) {
    const date = fmt
      ? DateTime.fromFormat(dirtyDate, fmt).toJSDate()
      : DateTime.fromISO(dirtyDate).toJSDate();
    return isValid(date) ? date : undefined;
  }
  return undefined;
};

export const parseDateRange = (
  dirtyRange: {
    startDate: string;
    endDate: string;
    key: string;
  },
  nullable = false
): DateRange => {
  return {
    startDate:
      parseDateFromString(dirtyRange?.startDate) ||
      (nullable ? undefined : new Date()),
    endDate:
      parseDateFromString(dirtyRange?.endDate) ||
      (nullable ? undefined : new Date()),
  };
};

export const generateCurrentTime = (): string => {
  return new Date().toISOString().slice(-24).replace(/\D/g, "").slice(0, 16);
};

export const currencyFormat = (value: string, symbol = "$"): string => {
  return `${symbol}${value.replace(/\.?0+$/, "")}`;
};

export const currencyFormatWithCode = (
  value: string,
  code: string,
  symbol = "$"
): string => {
  return `${symbol}${value.replace(/\.?0+$/, "")}${code}`;
};

export const convertStringToTimeStr = (value: string): string => {
  if (!value) {
    return "";
  }

  const hh = value.slice(0, 2);
  const mm = value.slice(2, 4);
  const ss = value.slice(4, 6);

  return `${hh}:${mm}:${ss}`;
};

export const findSubmultipleOfNumber = (
  num: number,
  currentOption?: string
): OptionTypeBase[] => {
  const arr = [];
  for (let index = 1; index <= num; index++) {
    if (num % index === 0) arr.push(index);
  }
  if (
    currentOption &&
    arr.filter((x) => x === parseInt(currentOption, 10)).length === 0
  ) {
    arr.push(parseInt(currentOption, 10));
  }
  return arr.map((x) => {
    return {
      label: `${x}H`,
      value: `${x}H`,
    } as OptionTypeBase;
  });
};
export const formatTime = (
  date: Date,
  showAMPM = true,
  isNextDay = false
): string => {
  let hoursOfDate = date.getHours();
  const minutesOfDate = date.getMinutes();
  const timeFormat = hoursOfDate >= 12 ? "PM" : "AM";
  hoursOfDate %= 12;
  hoursOfDate = hoursOfDate || 12; // the hour '0' should be '12'
  const minutes = minutesOfDate < 10 ? `0${minutesOfDate}` : minutesOfDate;
  const hours = hoursOfDate < 10 ? `0${hoursOfDate}` : hoursOfDate;
  const resultTimeFormat = isNextDay
    ? `${hours}:${minutes}ND`
    : `${hours}:${minutes}`;
  const strTime = showAMPM
    ? `${resultTimeFormat} ${timeFormat}`
    : `${resultTimeFormat}`;
  return strTime;
};

export const getEndOfTheDay = (day?: Date | null): Date | null => {
  if (!day) return null;
  const result = new Date(day);
  result.setUTCHours(23, 59, 59, 999);
  return result;
};

export const getUrlOrganisation = (organisationId: string) => {
  const origin = window.location.origin;
  const domainArr = origin.split("https://");
  const domain = domainArr[1];
  const urlArr = domain.split(".");
  const sub = urlArr[0];
  if (sub === "clinician")
    return `https://${organisationId}.${window.location.host}`;
  else return origin.replace(sub, organisationId);
};

export const getTypeDomain = (): "individual" | "organisation" => {
  const origin = window.location.origin;
  const domainArr = origin.split("https://");
  const domain = domainArr[1];
  const urlArr = domain.split(".");
  const sub = urlArr[0];
  if (sub === "clinician") return "individual";
  return "organisation";
};

export const isIndividual = (): boolean => {
  return getTypeDomain() === "individual";
};

export const getSubDomain = () => {
  const host = window.location.host;
  const dm = host.split(".");
  return dm[0];
};

export const formatRegionUrl = (
  region?: string | null,
  isAuthenticated = false
): string => {
  const isDev = APP_ENVIRONMENT === "development";
  if (!region || !isAuthenticated) {
    return `https://${isDev ? "" : "api."}${API_BASE_URL}`;
  }
  return `https://${region}${isDev ? "" : ".api"}.${API_BASE_URL}`;
};

export const formatUnleashRegionUrl = (
  region: string,
  apiName = "api",
  isAuthenticated = false
): string => {
  if (!region || !isAuthenticated) {
    return `https://${apiName}.${API_BASE_URL}`;
  }
  return `https://${region}.${apiName}.${API_BASE_URL}`;
};

export const openWindowOpenOauthURL = (url: string) => {
  window.location.href = url;
};
